import Icon from '@/components/ui/AppIcon';

const VerificationProcess = () => {
  const steps = [
    {
      number: 1,
      title: 'Submit Application',
      description: 'Complete the claim form with business details and required documents',
      icon: 'DocumentTextIcon',
      status: 'current'
    },
    {
      number: 2,
      title: 'Document Review',
      description: 'Our team verifies your business license, insurance, and ownership proof',
      icon: 'MagnifyingGlassIcon',
      status: 'upcoming',
      duration: '1-2 business days'
    },
    {
      number: 3,
      title: 'Phone Verification',
      description: 'We call your business number to confirm operational status',
      icon: 'PhoneIcon',
      status: 'upcoming',
      duration: '1 business day'
    },
    {
      number: 4,
      title: 'Listing Activation',
      description: 'Your enhanced listing goes live with verified badge and full features',
      icon: 'CheckBadgeIcon',
      status: 'upcoming',
      duration: 'Immediate'
    }
  ];

  const benefits = [
    {
      icon: 'ChartBarIcon',
      title: 'Increased Visibility',
      description: 'Priority placement in search results for your service area'
    },
    {
      icon: 'UserGroupIcon',
      title: 'Quality Leads',
      description: 'Connect with BMW and Lexus owners actively seeking repairs'
    },
    {
      icon: 'ShieldCheckIcon',
      title: 'Trust Badge',
      description: 'Verified business status builds customer confidence'
    },
    {
      icon: 'CurrencyDollarIcon',
      title: 'Free Listing',
      description: 'No monthly fees or commissions on customer inquiries'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Verification Steps */}
      <div className="space-y-6">
        <div className="flex items-start space-x-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
            <Icon name="ClipboardDocumentCheckIcon" size={20} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
              Verification Process
            </h3>
            <p className="font-body text-sm text-text-secondary">
              Typical verification takes 3-5 business days
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {steps.map((step, index) => (
            <div key={step.number} className="relative">
              {index < steps.length - 1 && (
                <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-border" />
              )}
              <div className="flex items-start space-x-4">
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-full flex-shrink-0 ${
                    step.status === 'current' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground'
                  }`}
                >
                  <Icon name={step.icon as any} size={20} />
                </div>
                <div className="flex-1 pt-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-body text-base font-semibold text-text-primary">
                      {step.title}
                    </h4>
                    {step.duration && (
                      <span className="font-caption text-xs text-muted-foreground">
                        {step.duration}
                      </span>
                    )}
                  </div>
                  <p className="font-body text-sm text-text-secondary">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <div className="space-y-6 border-t border-border pt-8">
        <div className="flex items-start space-x-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 flex-shrink-0">
            <Icon name="SparklesIcon" size={20} className="text-accent" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
              Why Claim Your Listing?
            </h3>
            <p className="font-body text-sm text-text-secondary">
              Join verified mechanics serving the local
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="rounded-lg border border-border bg-card p-4 space-y-2"
            >
              <div className="flex items-center space-x-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <Icon name={benefit.icon as any} size={16} className="text-primary" />
                </div>
                <h4 className="font-body text-sm font-semibold text-text-primary">
                  {benefit.title}
                </h4>
              </div>
              <p className="font-caption text-xs text-text-secondary leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Support Contact */}
      <div className="rounded-lg bg-muted p-6">
        <div className="flex items-start space-x-3">
          <Icon name="QuestionMarkCircleIcon" size={24} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-body text-base font-semibold text-text-primary mb-2">
              Need Help?
            </h4>
            <p className="font-body text-sm text-text-secondary mb-4">
              Our verification team is here to assist you through the process.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:verify@autofixiq.com"
                className="inline-flex items-center space-x-2 rounded-lg border border-border bg-card px-4 py-2 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:text-primary"
              >
                <Icon name="EnvelopeIcon" size={16} />
                <span>verify@autofixiq.com</span>
              </a>
              <a
                href="tel:+15551234567"
                className="inline-flex items-center space-x-2 rounded-lg border border-border bg-card px-4 py-2 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:text-primary"
              >
                <Icon name="PhoneIcon" size={16} />
                <span>(555) 123-4567</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationProcess;